/**
 * SocketService - Single source of truth for WebSocket connection.
 * CONNECTED only after server event connectionSuccess (not on Socket.IO connect).
 * Listener-first: all listeners registered before socket.connect().
 * Aligned with FRONTEND_WEBSOCKET_COMPLETE_INTEGRATION_GUIDE.
 */

import { io } from 'socket.io-client';
import { getAccessTokenToCookies, getAccessToken } from '@/shared/utils/tokenHelper';
import {
  ConnectionState,
  ConnectionErrorCodes,
  SOCKET_OPTIONS,
  CONNECTION_SUCCESS_TIMEOUT_MS,
  RECONNECT_BACKOFF_INITIAL_MS,
  RECONNECT_BACKOFF_MAX_MS,
  RECONNECT_BACKOFF_MULTIPLIER,
  getSocketPath,
  getSocketUrl,
} from './constants';

/** Server events we forward to app listeners (doc §7, §8). Call events: backend CALL_API_FRONTEND_ALIGNMENT. */
const FORWARDED_EVENTS = [
  'newMessage', 'messageUpdated', 'messageDeleted', 'messageDeliveryStatus', 'messageRead',
  'userTyping', 'userStoppedTyping', 'conversationUpdated', 'newConversation',
  'conversationUpdatedInList', 'conversationListUpdate', 'conversationDeleted', 'conversationArchived', 'conversationInvited',
  'chatRequestReceived', 'chatRequestAccepted', 'chatRequestDeclined',
  'reactionUpdated', 'userStatusChanged', 'userOnline', 'userOffline',
  'friendStatusChanged', 'friendLoggedIn', 'friendLoggedOut', 'userLoggedOut', 'onlineUsers',
  'notification_created', 'notification_updated', 'notification_deleted',
  'messageMention', 'message:created',
  'conversationLeft', 'conversationRemoved',
  'groupChatMuted', 'groupChatUnmuted',
  'video_processing_complete', 'video_processing_failed',
  'user:restricted', 'user:unrestricted',
  'call:incoming', 'call:initiated', 'call:accepted', 'call:rejected', 'call:cancelled', 'call:ended', 'call:transferred', 'call:busy', 'call:error',
  'call:answered_elsewhere', 'call:rejected_elsewhere',
  // Live streaming (see backend LIVE_STREAM_QUICKSTART §9)
  'live:comment:new', 'live:comment:pinned', 'live:comment:deleted',
  'live:reaction', 'live:viewer:joined', 'live:viewer:left',
  'live:layout:update', 'live:typing',
  'live:viewer:muted', 'live:viewer:unmuted', 'live:viewer:kicked', 'live:viewer:banned',
  'live:milestone', 'live:settings:updated', 'live:ended',
  'live:streamer:disconnected', 'live:streamer:reconnected',
  'live:muted', 'live:unmuted', 'live:kicked', 'live:banned',
  'live:max-duration-warning', 'live:max-duration-reached',
  'live:guest:invite', 'live:guest:token:refresh', 'live:guest:joined', 'live:guest:removed',
  'live:guest:force-muted', 'live:guest:audio:muted', 'live:guest:audio:unmuted',
  'live:guest:video:muted', 'live:guest:video:unmuted',
  'live:speak:request', 'live:speak:request:removed', 'live:speak:rejected',
  'live:guest:declined',
  'live:started',
  'live:moderator:promoted', 'live:moderator:demoted',
  'live:recording:failed', 'live:terminated-by-moderator', 'live:unbanned',
  'feed:live-post-created',
  'newNotification', 'conversationPositionUpdate', 'system_alert',
  'sessionExpired',
  'auth_error', 'autoJoinConversation',
];

export class SocketService {
  constructor() {
    this._socket = null;
    this._state = ConnectionState.DISCONNECTED;
    this._pendingJoinConversationId = null;
    this._currentOpenConversationId = null;
    /** @type {Set<string>} Active conversation rooms (supports modal + fullscreen). */
    this._joinedConversationIds = new Set();
    this._reconnectTimeoutId = null;
    this._nextReconnectDelayMs = null;
    this._connectionSuccessTimeoutId = null;
    this._reconnectAttempt = 0;
    this._intentionalDisconnect = false;
    this._listeners = new Map();
    this._stateChangeCallbacks = new Set();
    this._tokenGracePeriodActive = false;
    this._tokenGracePeriodTimeoutId = null;
    this._tokenRefreshReconnect = false;
    this._graceTokenPushDone = false;
    /** Same-device newer socket won; block passive reconnect until explicit login/reload. */
    this._sessionSuperseded = false;
  }

  _getAuthToken() {
    // Cookie mode: httpOnly cookies are not readable here; WS auth needs handshake.auth.token (see integration guide).
    const useCookies = process.env.VUE_APP_USE_COOKIES === 'true';
    const token = useCookies ? getAccessTokenToCookies() : getAccessToken();
    if (!token || token === 'null' || token === 'undefined' || String(token).trim() === '') {
      return null;
    }
    return token;
  }

  _getSocketOptions() {
    return {
      ...SOCKET_OPTIONS,
      autoConnect: false,
      path: getSocketPath(),
      auth: (cb) => {
        cb({ token: this._getAuthToken() || null });
      },
    };
  }

  _setState(newState) {
    if (this._state === newState) return;
    const oldState = this._state;
    this._state = newState;
    this._stateChangeCallbacks.forEach((cb) => {
      try {
        cb(newState, oldState);
      } catch (e) {
        // ignore
      }
    });
  }

  _emitToListeners(eventName, data) {
    const set = this._listeners.get(eventName);
    if (!set) return;
    set.forEach((cb) => {
      try {
        cb(data);
      } catch (e) {
        console.error('[SocketService] Listener error for', eventName, e);
      }
    });
  }

  _registerAllListeners(socket) {
    socket.on('connect', () => {
      this._setState(ConnectionState.CONNECTING);
      this._emitToListeners('connect');
      this._connectionSuccessTimeoutId = setTimeout(() => {
        this._onConnectionSuccessTimeout();
      }, CONNECTION_SUCCESS_TIMEOUT_MS);
    });

    socket.on('disconnect', (reason) => {
      if (this._connectionSuccessTimeoutId) {
        clearTimeout(this._connectionSuccessTimeoutId);
        this._connectionSuccessTimeoutId = null;
      }
      if (reason === 'io client disconnect' && !this._tokenRefreshReconnect) {
        this._intentionalDisconnect = true;
        this._currentOpenConversationId = null;
        this._joinedConversationIds.clear();
      }
      this._setState(ConnectionState.DISCONNECTED);
      this._releaseSocket(socket);
      this._emitToListeners('disconnect', reason);
      if (!this._intentionalDisconnect) {
        this._scheduleReconnect();
      }
    });

    socket.on('connect_error', (err) => {
      if (this._connectionSuccessTimeoutId) {
        clearTimeout(this._connectionSuccessTimeoutId);
        this._connectionSuccessTimeoutId = null;
      }
      this._setState(ConnectionState.DISCONNECTED);
      this._teardownSocket();
      this._emitToListeners('connect_error', err);
      if (!this._intentionalDisconnect) {
        this._scheduleReconnect();
      }
    });

    socket.on('connectionSuccess', (payload) => {
      this._cancelScheduledReconnect();
      if (this._connectionSuccessTimeoutId) {
        clearTimeout(this._connectionSuccessTimeoutId);
        this._connectionSuccessTimeoutId = null;
      }
      const reconnectAttempt = this._reconnectAttempt;
      this._reconnectAttempt = 0;
      this._setState(ConnectionState.CONNECTED);
      if (reconnectAttempt > 0) {
        this._emitToListeners('reconnect', reconnectAttempt);
      }
      this._rejoinAllConversations();
      this._emitToListeners('connectionSuccess', payload);
    });

    socket.on('connectionError', (payload) => {
      this._handleConnectionError(payload);
    });

    socket.on('tokenRefreshSuccess', (payload) => {
      this._clearTokenGracePeriod();
      this._emitToListeners('tokenRefreshSuccess', payload);
    });

    socket.on('tokenRefreshError', (payload) => {
      this._clearTokenGracePeriod();
      this._emitToListeners('tokenRefreshError', payload);
    });

    socket.on('session:replaced', (payload) => {
      const code = payload?.code;
      if (code === 'WS_SESSION_REPLACED') {
        this._sessionSuperseded = true;
        this._intentionalDisconnect = true;
        this._pendingJoinConversationId = null;
        this._currentOpenConversationId = null;
        this._joinedConversationIds.clear();
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
        this._emitToListeners('session:replaced', payload);
        return;
      }
      if (code === 'WS_SESSION_EVICTED' || payload?.requiresReconnect === true) {
        const delay = payload?.retryAfterMs ?? 30000;
        this.setReconnectDelay(delay);
        this._intentionalDisconnect = false;
        this._cancelScheduledReconnect();
        if (this._socket?.connected || this._state === ConnectionState.CONNECTED) {
          this._teardownSocket();
          this._setState(ConnectionState.DISCONNECTED);
        }
        this._scheduleReconnect();
      }
      this._emitToListeners('session:replaced', payload);
    });

    FORWARDED_EVENTS.forEach((eventName) => {
      socket.on(eventName, (data) => {
        this._emitToListeners(eventName, data);
      });
    });
  }

  _onConnectionSuccessTimeout() {
    this._connectionSuccessTimeoutId = null;
    this._setState(ConnectionState.DISCONNECTED);
    this._teardownSocket();
    if (!this._intentionalDisconnect) {
      this._scheduleReconnect();
    }
  }

  _rejoinAllConversations() {
    if (!this._socket || this._state !== ConnectionState.CONNECTED) return;

    const pending = this._pendingJoinConversationId;
    if (pending) {
      this._joinedConversationIds.add(pending);
      this._pendingJoinConversationId = null;
    }

    const ids = this._joinedConversationIds.size > 0
      ? [...this._joinedConversationIds]
      : (this._currentOpenConversationId ? [this._currentOpenConversationId] : []);

    ids.forEach((conversationId) => {
      this._socket.emit('joinConversation', { conversationId });
    });

    if (ids.length > 0) {
      this._currentOpenConversationId = ids[ids.length - 1];
    }
  }

  /** Server-provided backoff (e.g. WS_SESSION_EVICTED retryAfterMs). */
  setReconnectDelay(ms) {
    if (typeof ms === 'number' && ms > 0) {
      this._nextReconnectDelayMs = ms;
    }
  }

  _scheduleReconnect() {
    if (this._state === ConnectionState.CONNECTED) return;
    if (this._intentionalDisconnect) return;
    this._setState(ConnectionState.RECONNECTING);
    const calculated = Math.min(
      RECONNECT_BACKOFF_INITIAL_MS * Math.pow(RECONNECT_BACKOFF_MULTIPLIER, this._reconnectAttempt),
      RECONNECT_BACKOFF_MAX_MS
    );
    const serverDelay = this._nextReconnectDelayMs;
    this._nextReconnectDelayMs = null;
    const delay = serverDelay != null ? Math.max(serverDelay, calculated) : calculated;
    this._reconnectAttempt += 1;
    this._reconnectTimeoutId = setTimeout(() => {
      this._reconnectTimeoutId = null;
      this._teardownSocket();
      this._createSocket();
    }, delay);
  }

  _cancelScheduledReconnect() {
    if (this._reconnectTimeoutId) {
      clearTimeout(this._reconnectTimeoutId);
      this._reconnectTimeoutId = null;
    }
  }

  /** Drop a socket reference after disconnect without double-disconnecting. */
  _releaseSocket(socket) {
    if (socket) {
      socket.removeAllListeners();
    }
    if (this._socket === socket) {
      this._socket = null;
    }
  }

  _teardownSocket() {
    this._cancelScheduledReconnect();
    if (this._connectionSuccessTimeoutId) {
      clearTimeout(this._connectionSuccessTimeoutId);
      this._connectionSuccessTimeoutId = null;
    }
    if (this._socket) {
      this._socket.removeAllListeners();
      this._socket.disconnect();
      this._socket = null;
    }
  }

  _clearTokenGracePeriod() {
    this._tokenGracePeriodActive = false;
    this._graceTokenPushDone = false;
    if (this._tokenGracePeriodTimeoutId) {
      clearTimeout(this._tokenGracePeriodTimeoutId);
      this._tokenGracePeriodTimeoutId = null;
    }
  }

  _beginTokenGracePeriod(gracePeriodMs) {
    this._tokenGracePeriodActive = true;
    if (this._tokenGracePeriodTimeoutId) {
      clearTimeout(this._tokenGracePeriodTimeoutId);
    }
    this._tokenGracePeriodTimeoutId = setTimeout(() => {
      this._clearTokenGracePeriod();
    }, gracePeriodMs + 2000);
  }

  async _pushTokenDuringGracePeriod(gracePeriodMs) {
    this._beginTokenGracePeriod(gracePeriodMs);
    this._graceTokenPushDone = false;

    try {
      const { checkAndRefreshToken } = await import('@/shared/services/tokenRefreshService');
      await checkAndRefreshToken();
    } catch {
      // REST refresh is best-effort
    }

    const token = this._getAuthToken();
    if (token && this._socket && !this._graceTokenPushDone) {
      this._socket.emit('refreshToken', { accessToken: token });
      this._graceTokenPushDone = true;
    }
  }

  _handleConnectionError(payload) {
    const code = payload?.code || '';
    const noRetry = { code, message: payload?.message, noRetry: true };
    const withRetry = { code, message: payload?.message, gracePeriodMs: payload?.gracePeriodMs, refreshEndpoint: payload?.refreshEndpoint };

    switch (code) {
      case ConnectionErrorCodes.WS_TOKEN_EXPIRED: {
        const gracePeriodMs = payload?.gracePeriodMs;
        if (gracePeriodMs && this._socket) {
          this._pushTokenDuringGracePeriod(gracePeriodMs);
          this._emitToListeners('connectionError', { ...withRetry });
          return;
        }
        this._emitToListeners('connectionError', withRetry);
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
        if (!this._intentionalDisconnect) this._scheduleReconnect();
        break;
      }
      case ConnectionErrorCodes.WS_TOKEN_REFRESH_TIMEOUT:
      case ConnectionErrorCodes.WS_TOKEN_INVALID:
      case ConnectionErrorCodes.WS_SESSION_INVALID:
      case ConnectionErrorCodes.WS_USER_NOT_FOUND:
        this._emitToListeners('connectionError', noRetry);
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
        break;
      case ConnectionErrorCodes.WS_USER_INACTIVE:
        this._emitToListeners('connectionError', noRetry);
        this._intentionalDisconnect = true;
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
        break;
      case ConnectionErrorCodes.WS_SESSION_EXPIRED:
        this._emitToListeners('connectionError', noRetry);
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
        break;
      case ConnectionErrorCodes.WS_TOO_MANY_CONNECTIONS: {
        const retryAfterMs = payload?.retryAfterMs ?? 30000;
        this._emitToListeners('connectionError', { ...withRetry, retryAfterMs, noRetry: false });
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
        this._nextReconnectDelayMs = retryAfterMs;
        if (!this._intentionalDisconnect) this._scheduleReconnect();
        break;
      }
      case ConnectionErrorCodes.WS_CONNECTION_FAILED:
      case ConnectionErrorCodes.WS_AUTH_FAILED:
        this._emitToListeners('connectionError', withRetry);
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
        if (!this._intentionalDisconnect) this._scheduleReconnect();
        break;
      default:
        this._emitToListeners('connectionError', withRetry);
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
        if (!this._intentionalDisconnect) this._scheduleReconnect();
    }
  }

  _createSocket() {
    if (this._socket) return;
    const url = getSocketUrl();
    const options = this._getSocketOptions();
    this._socket = io(url, options);
    this._registerAllListeners(this._socket);
    this._socket.connect();
  }

  connect() {
    if (this._sessionSuperseded) return;
    if (this._socket && (this._socket.connected || this._socket.connecting)) {
      return;
    }
    if (this._socket) {
      this._teardownSocket();
    }
    this._intentionalDisconnect = false;
    if (!this._getAuthToken()) {
      return;
    }
    this._createSocket();
  }

  disconnect() {
    this._intentionalDisconnect = true;
    this._sessionSuperseded = false;
    this._teardownSocket();
    this._setState(ConnectionState.DISCONNECTED);
    this._pendingJoinConversationId = null;
    this._currentOpenConversationId = null;
    this._joinedConversationIds.clear();
    this._nextReconnectDelayMs = null;
  }

  getState() {
    return this._state;
  }

  isConnected() {
    return this._state === ConnectionState.CONNECTED && this._socket?.connected;
  }

  on(event, callback) {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event).add(callback);
  }

  off(event, callback) {
    const set = this._listeners.get(event);
    if (set) {
      set.delete(callback);
      if (set.size === 0) this._listeners.delete(event);
    }
  }

  emit(event, data, ack) {
    if (!this._socket) return;
    const conversationEvents = ['joinConversation', 'leaveConversation', 'typingStart', 'typingStop', 'messageDelivered', 'messageRead'];
    const needsConnected = conversationEvents.includes(event);
    if (needsConnected && this._state !== ConnectionState.CONNECTED) return;
    if (typeof ack === 'function') {
      this._socket.emit(event, data, ack);
    } else {
      this._socket.emit(event, data);
    }
  }

  onStateChange(callback) {
    this._stateChangeCallbacks.add(callback);
    try {
      callback(this._state);
    } catch (e) {
      // ignore
    }
    return () => {
      this._stateChangeCallbacks.delete(callback);
    };
  }

  offStateChange(callback) {
    this._stateChangeCallbacks.delete(callback);
  }

  requestJoinConversation(conversationId) {
    if (!conversationId) return;
    this._joinedConversationIds.add(conversationId);
    this._currentOpenConversationId = conversationId;
    if (this._state === ConnectionState.CONNECTED) {
      if (this._socket) {
        this._socket.emit('joinConversation', { conversationId });
      }
      return;
    }
    if (this._state === ConnectionState.CONNECTING || this._state === ConnectionState.RECONNECTING) {
      this._pendingJoinConversationId = conversationId;
      return;
    }
    if (this._state === ConnectionState.DISCONNECTED) {
      this._pendingJoinConversationId = conversationId;
      if (!this._socket && this._getAuthToken()) {
        this.connect();
      }
    }
  }

  leaveConversation(conversationId) {
    this._joinedConversationIds.delete(conversationId);
    if (conversationId === this._currentOpenConversationId) {
      const remaining = [...this._joinedConversationIds];
      this._currentOpenConversationId = remaining.length ? remaining[remaining.length - 1] : null;
    }
    if (this._state === ConnectionState.CONNECTED && this._socket) {
      this._socket.emit('leaveConversation', { conversationId });
    }
  }

  /**
   * Re-handshake with the latest JWT while preserving open conversation context.
   * Proactive REST refresh uses this path; server refreshToken WS only works in grace period.
   */
  reconnectWithFreshToken() {
    if (!this._getAuthToken()) return;

    this._sessionSuperseded = false;
    const preservedIds = this._joinedConversationIds.size > 0
      ? [...this._joinedConversationIds]
      : [this._currentOpenConversationId || this._pendingJoinConversationId].filter(Boolean);
    this._intentionalDisconnect = false;
    this._cancelScheduledReconnect();
    this._tokenRefreshReconnect = true;
    this._teardownSocket();
    this._tokenRefreshReconnect = false;
    this._setState(ConnectionState.DISCONNECTED);

    this._joinedConversationIds = new Set(preservedIds);
    if (preservedIds.length > 0) {
      this._pendingJoinConversationId = preservedIds[preservedIds.length - 1];
      this._currentOpenConversationId = preservedIds[preservedIds.length - 1];
    }

    this.connect();
  }

  updateToken(token) {
    const t = token ?? this._getAuthToken();
    if (!t) return;

    if (this._socket?.io?.opts?.query) {
      this._socket.io.opts.query = { token: t };
    }

    if (this._state === ConnectionState.CONNECTED && this._socket?.connected) {
      try {
        this._socket.emit('refreshToken', { accessToken: t });
        this._graceTokenPushDone = true;
      } catch {
        // best-effort in-band token push; full reconnect reserved for login / grace failure
      }
      return;
    }

    if (this._state === ConnectionState.DISCONNECTED && !this._socket) {
      this.connect();
    }
  }

  isSessionSuperseded() {
    return this._sessionSuperseded;
  }

  getSocket() {
    return this._socket;
  }
}

let socketServiceInstance = null;

export function getSocketServiceInstance() {
  if (!socketServiceInstance) {
    socketServiceInstance = new SocketService();
  }
  return socketServiceInstance;
}
